import { getConfig } from '@/tools/config'
import { getDeviceUrn } from '@/tools/device-detection'
import { glovoRum } from '@/tools/o11y/client'
import type { UserModel } from '@/user/entities'

let isInitialised = false

const loadIncogniaSdk = async (): Promise<IncogniaWebSdk> => {
  const { incogniaSdkKey, incogniaSdkUrl, env } = getConfig()

  return new Promise((resolve, reject) => {
    if (window?.IncogniaWebSdk) {
      return resolve(window?.IncogniaWebSdk)
    }

    const script = document.createElement('script')
    script.src = incogniaSdkUrl
    script.defer = true
    script.setAttribute('incognia-app-id', incogniaSdkKey)
    if (env === 'development') {
      script.setAttribute('incognia-log-enabled', 'true')
    }
    document.head.appendChild(script)

    script.onload = () => {
      if (!window?.IncogniaWebSdk) {
        return reject(new Error('Incognia Web SDK failed to load'))
      }
      resolve(window?.IncogniaWebSdk)
    }
    script.onerror = (err) => reject(err)
  })
}

export const initIncognia = async () => {
  const { isIncogniaEnabled } = getConfig()

  if (!isIncogniaEnabled) {
    return
  }
  await loadIncogniaSdk()
  isInitialised = true
}

const hashPhoneNumber = async (phoneNumber?: UserModel['phoneNumber']) => {
  if (!phoneNumber) {
    return ''
  }

  const input = `+${phoneNumber.countryCode} ${phoneNumber.number}`
  const encoder = new TextEncoder()
  const data = encoder.encode(input)

  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

export const trackLogin = async (userId: number, phoneNumber: UserModel['phoneNumber']) => {
  if (!isInitialised) {
    await initIncognia()
  }
  const deviceId = getDeviceUrn()
  await window?.IncogniaWebSdk?.setAccountId(String(userId))
  const phoneNumberHash = await hashPhoneNumber(phoneNumber)
  await window?.IncogniaWebSdk?.sendCustomEvent({
    tag: 'login-monitoring',
    properties: {
      status: 'AUTHORIZED',
      phone_hash: phoneNumberHash,
      device_id: deviceId || '',
    },
  })
}

export const trackRegistration = async (userId: number, phoneNumber: UserModel['phoneNumber']) => {
  try {
    if (!isInitialised) {
      await initIncognia()
    }
    const deviceId = getDeviceUrn()
    await window?.IncogniaWebSdk?.setAccountId(String(userId))
    const phoneNumberHash = await hashPhoneNumber(phoneNumber)
    await window?.IncogniaWebSdk?.sendCustomEvent({
      tag: 'signup-monitoring',
      properties: {
        status: 'AUTHORIZED',
        phone_hash: phoneNumberHash,
        device_id: deviceId || '',
      },
    })
    // eslint-disable-next-line @glovo/custom-rules/always-forward-error
  } catch (e) {
    glovoRum.addError(e as Error, {})
  }
}

export const clearUserId = async () => {
  if (!isInitialised) {
    await initIncognia()
  }
  await window?.IncogniaWebSdk?.clearAccountId()
}
